"use client";

import { useCallback, useState } from "react";

interface NpsCooldownActions {
  /** Server action that stamps the full post-survey cooldown. */
  setNpsCooldown: () => Promise<{ success: boolean; error?: string }>;
  /** Server action that stamps a "Not now" deferral. */
  setNpsDeferral: () => Promise<{ success: boolean; error?: string }>;
}

/**
 * Hook for managing NPS survey cooldown via the app's server actions.
 *
 * When the survey is completed or dismissed, the app's action updates the
 * account with the current timestamp. `setDeferral` records a "Not now" that
 * defers re-prompting for a shorter window than the full cooldown, for
 * surveys that offer that option.
 *
 * The actions are injected because server actions live in the consuming app
 * (they need its account resolution and database access).
 */
export function useNpsCooldown(actions: NpsCooldownActions) {
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Sets the cooldown by calling the app's server action.
   */
  const setCooldown = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const result = await actions.setNpsCooldown();
      if (!result.success) {
        console.error("Failed to set NPS cooldown:", result.error);
      }
    } catch (error) {
      console.error("Error calling setNpsCooldown:", error);
    } finally {
      setIsUpdating(false);
    }
  }, [isUpdating, actions]);

  /**
   * Records a "Not now" deferral via the app's server action.
   */
  const setDeferral = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const result = await actions.setNpsDeferral();
      if (!result.success) {
        console.error("Failed to set NPS deferral:", result.error);
      }
    } catch (error) {
      console.error("Error calling setNpsDeferral:", error);
    } finally {
      setIsUpdating(false);
    }
  }, [isUpdating, actions]);

  return {
    setCooldown,
    setDeferral,
    isUpdating,
  };
}
